import React from 'react';
import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  AUTO_PREQUAL_WELCOME_COBORROWER,
  AUTO_PREQUAL_WELCOME_COBORROWER_EMPLOYMENT_INFO,
  AUTO_PREQUAL_WELCOME_COBORROWER_EMPLOYMENT_TYPE,
  AUTO_PREQUAL_WELCOME_CREDIT_AUTHORIZATION,
  AUTO_PREQUAL_WELCOME_CTA,
  AUTO_PREQUAL_WELCOME_EMPLOYMENT_INFO,
  AUTO_PREQUAL_WELCOME_EMPLOYMENT_TYPE,
  AUTO_PREQUAL_WELCOME_FINANCIAL_HISTORY,
  AUTO_PREQUAL_WELCOME_LIVING_PROPERTY,
  AUTO_PREQUAL_WELCOME_PREQUAL_AMOUNT,
  AUTO_PREQUAL_WELCOME_PREQUAL_SUMMARY,
  AUTO_PREQUAL_WELCOME_REAL_ESTATE_LIABILITIES,
  AUTO_PREQUAL_WELCOME_VETERAN,
} from 'app/models/sections/constants';
import { autoPrequalWelcomeQuestionsByName } from 'app/models/questions/auto-prequal-welcome';
import { ContactWelcome } from 'app/components/ContactWelcome';
import { AuthorizeCreditReport } from 'app/components/FormAgreements/AuthorizeCreditReport';
import * as FormActions from 'app/actions/form/actions';
import { goToSection } from 'app/actions/form/actions';
import { ajaxValidateBorrowerResidence, ajaxValidateCoBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { ajaxPostCreditReportForAutoPrequal } from 'app/actions/form/credit-report/actions';
import { FormParagraphs } from 'app/models/paragraphs';
import { PrequalSummary } from 'app/components/PrequalSummary/PrequalSummary';
import { PrequalSummaryHeader } from 'app/components/PrequalSummary/PrequalSummaryHeader';
import {
  LoanOfficerCondition,
  hasCoBorrower,
  hasRealEstateLiabilities,
  isCoBorrowerNotEmployed,
  isCoBorrowerEmployee,
  isCoBorrowerEmploymentOther,
  isCoBorrowerRetired,
  isCoBorrowerSelfEmployed,
  coBorrowerHasDifferentAddress,
  isCoBorrowerIncomeVerified,
  isBorrowerIncomeVerified,
} from 'app/models/fields/conditionals';
import { getAutoPrequalDeclarationQuestions } from 'app/models/questions/declarations';

export const getAutoPrequalWelcomeSectionById = (id: number): Section => {
  return AUTO_PREQUAL_WELCOME_SECTIONS.find((section) => section.id === id) as Section;
};

const AUTO_PREQUAL_WELCOME_SECTIONS: Section[] = [
  {
    id              : AUTO_PREQUAL_WELCOME_CTA,
    ifNoQuestions   : ContactWelcome,
    hideProgressBar : true,
    hideBackButton  : true,
    submitAction    : () => goToSection(AUTO_PREQUAL_WELCOME_LIVING_PROPERTY),
  },
  {
    id       : AUTO_PREQUAL_WELCOME_LIVING_PROPERTY,
    questions: autoPrequalWelcomeQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.maritalStatus,
      FieldNames.coBorrowerYN,
    ]),
    submitAction: ajaxValidateBorrowerResidence,
  },
  {
    id       : AUTO_PREQUAL_WELCOME_COBORROWER,
    title    : 'Enter your co-borrower\'s personal information',
    questions: autoPrequalWelcomeQuestionsByName([
      FieldNames.coBorrowerFirstName,
      FieldNames.coBorrowerMiddleName,
      FieldNames.coBorrowerLastName,
      FieldNames.coBorrowerSuffixName,
      FieldNames.coBorrowerDOB,
      FieldNames.coBorrowerSameAddress,
      FieldNames.coBorrowerLivingPropertyStreet,
      FieldNames.coBorrowerLivingPropertyStreet2,
      FieldNames.coBorrowerLivingPropertyCity,
      FieldNames.coBorrowerLivingPropertyState,
      FieldNames.coBorrowerLivingPropertyZip,
    ]),
    showIf      : [hasCoBorrower],
    submitAction: (formValues) =>
      coBorrowerHasDifferentAddress(formValues) ? ajaxValidateCoBorrowerResidence(formValues) : ajaxValidateBorrowerResidence(formValues),
  },
  {
    id       : AUTO_PREQUAL_WELCOME_VETERAN,
    questions: autoPrequalWelcomeQuestionsByName([
      FieldNames.veteranYN,
      FieldNames.veteranLoanYN,
    ]),
    conditionalIf: {
      referToLoIf: [LoanOfficerCondition.isVeteranLoan],
    },
  },
  {
    id       : AUTO_PREQUAL_WELCOME_CREDIT_AUTHORIZATION,
    questions: autoPrequalWelcomeQuestionsByName([
      FieldNames.loanOfficer,
    ]),
    agreement: <AuthorizeCreditReport />,
  },
  {
    id            : AUTO_PREQUAL_WELCOME_FINANCIAL_HISTORY,
    title         : 'A few more questions about your financial history',
    questions     : getAutoPrequalDeclarationQuestions(),
    conditionalIf : {
      referToLoIf: [
        LoanOfficerCondition.hasBankrupt,
        LoanOfficerCondition.hasForeclosed,
        LoanOfficerCondition.isBorrowerNotCitizen,
      ],
    },
    submitAction  : ajaxPostCreditReportForAutoPrequal,
    showLoanOfficer: true,
  },
  {
    id             : AUTO_PREQUAL_WELCOME_REAL_ESTATE_LIABILITIES,
    title          : 'Real Estate Liabilities',
    paragraph      : FormParagraphs.realEstateLiabilities,
    questions      : autoPrequalWelcomeQuestionsByName([FieldNames.realEstateLiabilities]),
    showLoanOfficer: true,
    showIf         : [hasRealEstateLiabilities],
  },
  {
    id             : AUTO_PREQUAL_WELCOME_EMPLOYMENT_TYPE,
    questions      : autoPrequalWelcomeQuestionsByName([FieldNames.employmentType]),
    showLoanOfficer: true,
  },
  {
    id             : AUTO_PREQUAL_WELCOME_EMPLOYMENT_INFO,
    title          : 'Tell us about your income',
    questions      : autoPrequalWelcomeQuestionsByName([
      FieldNames.employCompanyName,
      FieldNames.employBase,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
      FieldNames.incomeVerifiable,
    ]),
    showLoanOfficer: true,
    submitAction   : FormActions.handleAutoPrequalGetRates,
    submitIf       : [(values) => !hasCoBorrower(values) && isBorrowerIncomeVerified(values)],
    submitText     : 'Submit',
    conditionalIf  : {
      referToLoIf: [LoanOfficerCondition.isBorrowerIncomeUnverified],
    },
  },
  {
    id             : AUTO_PREQUAL_WELCOME_COBORROWER_EMPLOYMENT_TYPE,
    questions      : autoPrequalWelcomeQuestionsByName([FieldNames.coBorrowerEmploymentType]),
    showLoanOfficer: true,
    showIf         : [hasCoBorrower],
    submitAction   : FormActions.handleAutoPrequalGetRates,
    submitIf       : [isCoBorrowerNotEmployed],
    submitText     : 'Submit',
  },
  {
    id             : AUTO_PREQUAL_WELCOME_COBORROWER_EMPLOYMENT_INFO,
    title          : 'Tell us about your co-borrower\'s income',
    questions      : autoPrequalWelcomeQuestionsByName([
      FieldNames.coBorrowerEmployCompanyName,
      FieldNames.coBorrowerEmployBase,
      FieldNames.coBorrowerEmployOvertime,
      FieldNames.coBorrowerEmployCommission,
      FieldNames.coBorrowerEmployBonus,
      FieldNames.coBorrowerAdditionalIncome,
      FieldNames.coBorrowerRetirementIncome,
      FieldNames.coBorrowerOtherIncome,
      FieldNames.coBorrowerIncomeVerifiable,
    ]),
    showLoanOfficer: true,
    showIf         : [
      isCoBorrowerEmployee,
      isCoBorrowerEmploymentOther,
      isCoBorrowerRetired,
      isCoBorrowerSelfEmployed,
    ],
    submitAction   : FormActions.handleAutoPrequalGetRates,
    submitText     : 'Submit',
    submitIf       : [(values) => isCoBorrowerIncomeVerified(values)],
    conditionalIf  : {
      referToLoIf: [LoanOfficerCondition.isCoBorrowerIncomeUnverified],
    },
  },
  {
    id                : AUTO_PREQUAL_WELCOME_PREQUAL_AMOUNT,
    questions         : autoPrequalWelcomeQuestionsByName([FieldNames.prequalAmount]),
    centerSubmitButton: true,
    fixedSubmitButton : true,
    hideBackButton    : true,
    hidePartialButton : true,
    removePadding     : true,
    submitAction      : FormActions.handleAutoPrequalSubmit,
    submitText        : 'Get Your Pre-Qualification Letter',
  },
  {
    id               : AUTO_PREQUAL_WELCOME_PREQUAL_SUMMARY,
    hideBackButton   : true,
    hideForwardButton: true,
    hideProgressBar  : true,
    ifNoQuestions    : PrequalSummary,
    removePadding    : true,
    showLoanOfficer  : true,
    useTopSection    : <PrequalSummaryHeader />,
  },
];
